import { useState, useEffect } from "react";
import { Github, Lock, Globe, GitBranch, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { api, type GitHubRepo } from "@/lib/api";
import { cn } from "@/lib/utils";

interface RepoPickerData {
  installation_id: string;
  project_id: string;
  repos?: GitHubRepo[];
}

interface GitHubRepoPickerWidgetProps {
  data: RepoPickerData;
}

export function GitHubRepoPickerWidget({ data }: GitHubRepoPickerWidgetProps) {
  const [repos, setRepos] = useState<GitHubRepo[]>(data.repos ?? []);
  const [loading, setLoading] = useState(!data.repos);
  const [connecting, setConnecting] = useState<number | null>(null);
  const [connected, setConnected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data.repos) return;
    api.github
      .repos(data.installation_id)
      .then(setRepos)
      .catch(() => setError("Failed to load repositories"))
      .finally(() => setLoading(false));
  }, [data.installation_id, data.repos]);

  async function handleSelect(repo: GitHubRepo) {
    setConnecting(repo.id);
    setError(null);
    try {
      await api.projects.connectRepo(data.project_id, {
        installation_id: data.installation_id,
        github_repo_id: repo.id,
        repo_full_name: repo.full_name,
        default_branch: repo.default_branch,
      });
      setConnected(repo.full_name);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to connect repository");
    } finally {
      setConnecting(null);
    }
  }

  if (connected) {
    return (
      <Card variant="success" padding="sm" className="max-w-md animate-slide-up">
        <div className="flex items-center gap-2">
          <Github size={14} className="text-daydra-success" />
          <p className="text-sm text-daydra-text">
            Connected to <span className="font-mono text-xs">{connected}</span>
          </p>
        </div>
      </Card>
    );
  }

  return (
    <Card variant="default" padding="none" className="overflow-hidden max-w-md animate-slide-up">
      <div className="flex items-center gap-3 px-4 py-3">
        <div className="flex items-center justify-center h-8 w-8 rounded-lg bg-daydra-accent-ghost">
          <Github size={16} className="text-daydra-accent" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-daydra-text">Select a repository</p>
          <p className="text-2xs text-daydra-muted mt-0.5">
            Choose which repo to connect to this project
          </p>
        </div>
      </div>

      <div className="border-t border-daydra-border max-h-64 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 size={16} className="text-daydra-dim animate-spin" />
          </div>
        ) : error ? (
          <div className="px-4 py-4 text-center">
            <p className="text-sm text-daydra-error">{error}</p>
          </div>
        ) : repos.length === 0 ? (
          <div className="px-4 py-4 text-center">
            <p className="text-sm text-daydra-muted">No repositories found</p>
          </div>
        ) : (
          <ul className="divide-y divide-daydra-border">
            {repos.map((repo) => (
              <li key={repo.id}>
                <button
                  onClick={() => handleSelect(repo)}
                  disabled={connecting !== null}
                  className={cn(
                    "w-full flex items-center gap-2 px-4 py-2.5 text-left",
                    "transition-colors duration-fast",
                    "hover:bg-daydra-raised disabled:opacity-50",
                  )}
                >
                  {repo.private ? (
                    <Lock size={12} className="text-daydra-warning shrink-0" />
                  ) : (
                    <Globe size={12} className="text-daydra-muted shrink-0" />
                  )}
                  <span className="flex-1 font-mono text-xs text-daydra-text truncate">
                    {repo.full_name}
                  </span>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <GitBranch size={10} className="text-daydra-muted" />
                    <span className="text-2xs text-daydra-muted">{repo.default_branch}</span>
                  </div>
                  {connecting === repo.id && (
                    <Loader2 size={12} className="text-daydra-accent animate-spin shrink-0" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Card>
  );
}
